import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import { portalAdminApi, MandantAdresse, DokumentFreigabeDto } from '../services/portalService';

interface PortalShareDialogProps {
  open: boolean;
  onClose: () => void;
  dokumentId: number;
  dokumentTitel: string;
  onSuccess?: () => void;
}

const kategorien = [
  'Rechnung',
  'Vertrag',
  'Bescheid',
  'Korrespondenz',
  'Beleg',
  'Bericht',
  'Sonstiges',
];

const PortalShareDialog: React.FC<PortalShareDialogProps> = ({
  open,
  onClose,
  dokumentId,
  dokumentTitel,
  onSuccess,
}) => {
  const [mandanten, setMandanten] = useState<MandantAdresse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<{
    adresseID: number | '';
    titel: string;
    beschreibung: string;
    kategorie: string;
  }>({
    adresseID: '',
    titel: '',
    beschreibung: '',
    kategorie: 'Sonstiges',
  });

  useEffect(() => {
    if (open) {
      loadMandanten();
      setFormData(prev => ({
        ...prev,
        titel: dokumentTitel,
      }));
      setError(null);
      setSuccess(false);
    }
  }, [open, dokumentTitel]);

  const loadMandanten = async () => {
    try {
      setLoading(true);
      const response = await portalAdminApi.getMandanten();
      setMandanten(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Mandanten:', err);
      setError('Fehler beim Laden der Mandanten-Liste');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.adresseID) {
      setError('Bitte wählen Sie einen Mandanten aus');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const dto: DokumentFreigabeDto = {
        dokumentID: dokumentId,
        adresseID: formData.adresseID as number,
        titel: formData.titel || undefined,
        beschreibung: formData.beschreibung || undefined,
        kategorie: formData.kategorie,
      };

      await portalAdminApi.dokumentFreigeben(dto);
      setSuccess(true);

      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Fehler beim Freigeben:', err);
      setError(err.response?.data?.message || err.response?.data || 'Fehler beim Freigeben des Dokuments');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        adresseID: '',
        titel: '',
        beschreibung: '',
        kategorie: 'Sonstiges',
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dokument für Portal freigeben</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              Dokument wurde erfolgreich für das Portal freigegeben!
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth required>
                <InputLabel>Mandant</InputLabel>
                <Select
                  value={formData.adresseID}
                  label="Mandant"
                  onChange={(e) =>
                    setFormData({ ...formData, adresseID: e.target.value as number })
                  }
                  disabled={submitting || success}
                >
                  <MenuItem value="">
                    <em>Bitte wählen...</em>
                  </MenuItem>
                  {mandanten.map((mandant) => (
                    <MenuItem key={mandant.adresseID} value={mandant.adresseID}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <Typography>
                          {mandant.firma || `${mandant.vorname} ${mandant.nachname}`}
                        </Typography>
                        {mandant.hatPortalZugang && (
                          <Chip label="Portal-Zugang" size="small" color="success" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {mandanten.length === 0 && !loading && (
                <Alert severity="info">
                  Keine Mandanten gefunden. Stellen Sie sicher, dass Adressen als "Mandant" markiert sind.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Titel"
                value={formData.titel}
                onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                disabled={submitting || success}
                helperText="Wird dem Mandanten im Portal angezeigt"
              />

              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                multiline
                rows={2}
                disabled={submitting || success}
                helperText="Optionale Beschreibung für den Mandanten"
              />

              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={formData.kategorie}
                  label="Kategorie"
                  onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })}
                  disabled={submitting || success}
                >
                  {kategorien.map((kat) => (
                    <MenuItem key={kat} value={kat}>
                      {kat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {success ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || loading || !formData.adresseID}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Wird freigegeben...' : 'Freigeben'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PortalShareDialog;
